import '@xyflow/react/dist/base.css';
import './style.css';

import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Controls,
  type Edge as Edge_,
  type Node as Node_,
  type OnEdgesChange,
  type OnNodesChange,
  ReactFlow,
} from '@xyflow/react';
import { memo } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Save } from '@/components/core';
import { Edge, Node } from '@/components/flow';
import { useAgentTree, useNodesAndEdges } from '@/hooks';

export type FlowDiagramProps = {
  agentId: string;
};

const IFlowDiagram = ({ agentId }: FlowDiagramProps) => {
  const agentTree = useAgentTree({ agentId });
  const { nodes: initialNodes, edges: initialEdges } = useNodesAndEdges(agentTree);
  const [nodes, setNodes] = useState<Node_[]>([]);
  const [edges, setEdges] = useState<Edge_[]>([]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes]
  );
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  useEffect(() => {
    if (!agentTree.isPending && agentTree.hash) {
      setNodes(initialNodes);
      setEdges(initialEdges);
    }
    // TODO: Cannot use initialNodes as dep, will cause infinite recursion
  }, [agentTree.isPending, agentTree.hash]);

  if (agentTree.isPending) return <Save message="Setting up agent builder ..." />;

  return (
    <div className="h-full w-full bg-gray-50">
      <ReactFlow
        colorMode="light"
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={{ node: Node }}
        edgeTypes={{ edge: Edge }}
        fitView
        fitViewOptions={{ maxZoom: 1 }}
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#CCCCCC" />
        <Controls className="rounded-sm border border-gray-200 bg-white text-gray-600" />
      </ReactFlow>
    </div>
  );
};

export const FlowDiagram = memo(IFlowDiagram) as typeof IFlowDiagram;
